import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import { getDoc, doc, updateDoc, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import SignatureCapture from '../components/estimates/SignatureCapture';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { ensureHttps } from '../utils/urlUtils';

export default function SignEstimate() {
  const { estimateId, token } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!estimateId || !token) {
          setError('Invalid signing link. Please contact the sender for a new link.');
          setLoading(false);
          return;
        }

        // Search for the estimate across all companies using collectionGroup
        // Estimates are now stored in /companies/{companyId}/estimates/{estimateId}
        let estimateData = null;
        let estimateDocRef = null;

        try {
          // Use collectionGroup to search across all companies' estimates subcollections
          // Query by signingToken (we have this from the URL)
          const estimatesQuery = query(
            collectionGroup(db, 'estimates'),
            where('signingToken', '==', token)
          );
          
          const estimatesSnapshot = await getDocs(estimatesQuery);
          
          // Find the estimate with matching ID
          for (const estimateDoc of estimatesSnapshot.docs) {
            if (estimateDoc.id === estimateId) {
              estimateData = { id: estimateDoc.id, ...estimateDoc.data() };
              estimateDocRef = estimateDoc.ref;
              break;
            }
          }
        } catch (searchError) {
          console.error('Error searching for estimate with collectionGroup:', searchError);
          // Check if it's an index error
          if (searchError.code === 'failed-precondition' && searchError.message.includes('index')) {
            console.warn('CollectionGroup index required for signing links.');
            // Extract the index creation link from the error message if available
            const indexLinkMatch = searchError.message.match(/https:\/\/console\.firebase\.google\.com[^\s)]+/);
            if (indexLinkMatch) {
              const indexLink = indexLinkMatch[0];
              console.warn('Index creation link:', indexLink);
              // Store the error with the link for display to user
              setError(`An index is required for estimate signing. Please create the index by clicking this link: ${indexLink}`);
              setLoading(false);
              return;
            } else {
              setError('An index is required for estimate signing. Please check the browser console for the index creation link, or contact support.');
              setLoading(false);
              return;
            }
          }
          // collectionGroup queries require an index - if it fails, try fallback
        }

        // Fallback: try old top-level collection (for backward compatibility)
        if (!estimateData) {
          try {
            const legacyEstimateRef = doc(db, 'estimates', estimateId);
            const legacyEstimateSnap = await getDoc(legacyEstimateRef);
            if (legacyEstimateSnap.exists()) {
              const legacyData = legacyEstimateSnap.data();
              // Verify token matches
              if (legacyData.signingToken === token) {
                estimateData = { id: legacyEstimateSnap.id, ...legacyData };
                estimateDocRef = legacyEstimateRef;
              }
            }
          } catch (legacyError) {
            console.error('Error trying legacy path:', legacyError);
          }
        }

        if (!estimateData) {
          setError('Estimate not found. The link may be invalid or expired.');
          setLoading(false);
          return;
        }

        // Validate the token matches (should already be validated, but double-check)
        if (!estimateData.signingToken || estimateData.signingToken !== token) {
          setError('Invalid signing token. Please contact the sender for a new link.');
          setLoading(false);
          return;
        }

        // Store the document reference for updates
        estimateData._ref = estimateDocRef;

        // Check if already signed
        if (estimateData.signatureUrl) {
          setSigned(true);
        }

        setEstimate(estimateData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching estimate:', err);
        setError('Failed to load estimate. Please try again later.');
        setLoading(false);
      }
    };

    if (estimateId && token) {
      fetchEstimate();
    }
  }, [estimateId, token]);

  const handleSaveSignature = async (signatureData) => {
    if (!estimate) return;

    try {
      // Use the stored reference if available, otherwise search for it again
      let estimateRefToUpdate = estimate._ref;
      
      if (!estimateRefToUpdate) {
        // Find the estimate again to get the correct path
        try {
          const estimatesQuery = query(
            collectionGroup(db, 'estimates'),
            where('signingToken', '==', token)
          );
          const estimatesSnapshot = await getDocs(estimatesQuery);
          
          // Find the estimate with matching ID
          for (const estimateDoc of estimatesSnapshot.docs) {
            if (estimateDoc.id === estimate.id) {
              estimateRefToUpdate = estimateDoc.ref;
              break;
            }
          }
        } catch (err) {
          console.error('Error finding estimate for update:', err);
        }
        
        // Fallback to legacy path
        if (!estimateRefToUpdate) {
          estimateRefToUpdate = doc(db, 'estimates', estimate.id);
        }
      }

      await updateDoc(estimateRefToUpdate, {
        signatureUrl: signatureData.signatureUrl,
        signedAt: signatureData.signedAt,
        signedByName: signatureData.signedByName,
        status: 'Approved',
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setEstimate(prev => ({
        ...prev,
        signatureUrl: signatureData.signatureUrl,
        signedAt: signatureData.signedAt,
        signedByName: signatureData.signedByName,
        status: 'Approved'
      }));

      setSigned(true);
      setSignatureDialogOpen(false);
    } catch (error) {
      console.error('Error saving signature:', error);
      throw error;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    // Check if error contains a Firebase Console link for index creation
    const indexLinkMatch = error.match(/https:\/\/console\.firebase\.google\.com[^\s)]+/);
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {indexLinkMatch && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ mb: 1 }}>
              This is a one-time setup. Click the link below to create the required index:
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              href={indexLinkMatch[0]} 
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 1 }}
            >
              Create Index
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Note: The index may take a few minutes to build. Once created, you can refresh this page.
            </Typography>
          </Alert>
        )}
      </Container>
    );
  }

  if (!estimate) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">Estimate not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Sign Estimate
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Estimate #{estimate.estimateId}
        </Typography>

        {signed && estimate.signatureUrl ? (
          <Alert severity="success" sx={{ mb: 4 }}>
            This estimate has already been signed. Thank you!
          </Alert>
        ) : (
          <>
            {/* Estimate Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Estimate Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography><strong>Customer:</strong> {estimate.customerName}</Typography>
              <Typography><strong>Date:</strong> {formatDate(estimate.createdAt)}</Typography>
              <Typography><strong>Total:</strong> ${estimate.total.toFixed(2)}</Typography>
              {estimate.description && (
                <Typography sx={{ mt: 1 }}><strong>Description:</strong> {estimate.description}</Typography>
              )}
            </Box>

            {/* Line Items */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Items
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {estimate.lineItems?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description || item.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          ${(item.quantity * item.unitPrice).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <Typography variant="h6">Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">${estimate.total.toFixed(2)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Signature Section */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => setSignatureDialogOpen(true)}
                sx={{ px: 4, py: 1.5 }}
              >
                Sign Estimate
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                By signing, you agree to the terms and conditions of this estimate.
              </Typography>
            </Box>
          </>
        )}

        {/* Signature Capture Dialog */}
        <SignatureCapture
          open={signatureDialogOpen}
          onClose={() => setSignatureDialogOpen(false)}
          onSave={handleSaveSignature}
          estimateId={estimate.id}
          existingSignature={estimate.signatureUrl}
        />
      </Paper>
    </Container>
  );
}

